import {
  AbstractControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { authActions } from '../../state/auth/auth.actions';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { CoreAppState } from '../../state/core-app.state';
import { countries, isValidEmail, states } from '@aa/ts/common';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';
import { selectAuth } from '../../state/auth/auth.reducer';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { APP_VERSION } from '../../components/app-bar/app-bar.component';
import { MatInputModule } from '@angular/material/input';

interface RegisterFormModel {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  password: string;
  termsOfServiceAgreement: boolean;
}

@Component({
  selector: 'aa-register-view',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    FormlyMaterialModule,
    FormlyModule,
    MatCardModule,
    MatButtonModule,
  ],
  templateUrl: './register-view.component.html',
  styleUrl: './register-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterViewComponent {
  appVersion = APP_VERSION;

  form = new FormGroup({});
  model: RegisterFormModel = {
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    password: '',
    termsOfServiceAgreement: false,
  };
  fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'flex-row-field-group',
      fieldGroup: [
        {
          key: 'firstName',
          type: 'input',
          props: {
            label: 'First Name',
            required: true,
          },
        },
        {
          key: 'lastName',
          type: 'input',
          props: {
            label: 'Last Name',
            required: true,
          },
        },
      ],
    },
    {
      key: 'username',
      type: 'input',
      props: {
        label: 'Username',
        required: true,
      },
    },
    {
      key: 'email',
      type: 'input',
      props: {
        label: 'Email address',
        required: true,
      },
      validators: {
        email: {
          expression: (c: AbstractControl) => !c.value || isValidEmail(c.value),
        },
      },
    },
    {
      key: 'addressLine1',
      type: 'input',
      props: {
        label: 'Address Line 1',
        type: 'text',
        required: true,
      },
    },
    {
      key: 'addressLine2',
      type: 'input',
      props: {
        label: 'Address Line 2',
        type: 'text',
      },
    },
    {
      fieldGroupClassName: 'flex-row-field-group',
      fieldGroup: [
        {
          key: 'city',
          type: 'input',
          props: {
            label: 'City',
            type: 'text',
            required: true,
          },
        },
        {
          key: 'state',
          type: 'select',
          props: {
            label: 'State',
            options: states.map((state) => ({
              label: state.name,
              value: state.code,
            })),
            required: true,
          },
        },
        {
          key: 'zipCode',
          type: 'input',
          props: {
            label: 'Zip Code',
            type: 'text',
            required: true,
          },
        },
      ],
    },
    {
      key: 'country',
      type: 'select',
      defaultValue: 'US',
      props: {
        label: 'Country',
        options: countries.map((country) => ({
          label: country.name,
          value: country.code,
        })),
        required: true,
        disabled: true,
      },
    },
    {
      key: 'password',
      type: 'input',
      props: {
        keydown: (field: FormlyFieldConfig, e: KeyboardEvent) => {
          if (e.key == 'Enter') {
            e.preventDefault();
            this.submit();
          }
        },
        label: 'Password',
        type: 'password',
        required: true,
      },
      validators: ['min-password'],
    },
    {
      fieldGroupClassName: 'flex-row-field-group custom-checkbox',
      fieldGroup: [
        {
          key: 'termsOfServiceAgreement',
          type: 'checkbox',
          defaultValue: false,
          className: 'min-width',
          props: {},
          wrappers: [],
        },
        {
          template:
            '<p>I have read and agree to <a href="/terms-of-service">Terms of Service</a> and <a href="/privacy-policy">Privacy Policy</a>',
        },
      ],
    },
  ];

  error$: Observable<string | null | undefined>;

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    private readonly store: Store<CoreAppState>,
    private readonly router: Router,
  ) {
    this.store.dispatch(authActions.clearError());
    if (isPlatformBrowser(this.platformId)) {
      this.store
        .select((s) => selectAuth(s).user)
        .pipe(takeUntilDestroyed())
        .subscribe((user) => {
          if (user) {
            this.router.navigate(['']);
          }
        });
    }
    this.error$ = this.store.select((s) => selectAuth(s).error);
  }

  submit() {
    if (this.form.valid && this.model.termsOfServiceAgreement == true) {
      const modelCopy = { ...this.model };
      delete (modelCopy as any).termsOfServiceAgreement;
      this.store.dispatch(authActions.registerCustomer(this.model));
    } else {
      this.form.markAllAsTouched();
    }
  }
}
